import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  createWheelTemplate,
  deleteWheelTemplate,
  getDailyPageErrorMessage,
  getDailyPagePayload,
  isDailyPageApiSuccess,
  listWheelTemplates,
  updateWheelTemplate,
} from '../../services/dailyPageApi';
import {
  SECTOR_TYPES,
  emptySector,
  emptyTemplateForm,
  weightPercents,
} from './dailyTasksUtils';

function WheelTemplatesPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTemplateForm());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listWheelTemplates();
      if (!isDailyPageApiSuccess(res)) {
        setError(getDailyPageErrorMessage(res, 'Failed to load wheel templates.'));
        return;
      }
      const payload = getDailyPagePayload(res) || {};
      const list = payload.templates || payload || [];
      setTemplates(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err.message || 'Failed to load templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyTemplateForm());
    setModalOpen(true);
  };

  const openEdit = (template) => {
    setEditing(template);
    setForm({
      title: template.title || '',
      spin_count_per_day: Number(template.spin_count_per_day ?? 1),
      is_enabled: template.is_enabled !== false,
      sectors: Array.isArray(template.sectors) && template.sectors.length
        ? template.sectors.map((s, i) => ({
            sort_order: s.sort_order ?? i + 1,
            sector_type: s.sector_type || 'fe_reward',
            label: s.label || '',
            fe_amount: s.fe_amount ?? null,
            weight: Number(s.weight ?? 0),
            image_url: s.image_url || null,
          }))
        : emptyTemplateForm().sectors,
    });
    setModalOpen(true);
  };

  const percents = weightPercents(form.sectors);

  const updateSector = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      sectors: prev.sectors.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setSnackbar({ open: true, message: 'Title is required.', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      const body = {
        title: form.title.trim(),
        spin_count_per_day: Math.max(0, Number(form.spin_count_per_day) || 0),
        is_enabled: !!form.is_enabled,
        sectors: form.sectors.map((s, i) => ({
          sort_order: s.sort_order ?? i + 1,
          sector_type: s.sector_type,
          label: s.label?.trim() || null,
          fe_amount: s.sector_type === 'fe_reward' ? Number(s.fe_amount) || 0 : null,
          weight: Number(s.weight) || 0,
          image_url: s.image_url?.trim() || null,
        })),
      };
      const res = editing?.id
        ? await updateWheelTemplate(editing.id, body)
        : await createWheelTemplate(body);
      if (!isDailyPageApiSuccess(res)) {
        setSnackbar({
          open: true,
          message: getDailyPageErrorMessage(res, 'Failed to save template.'),
          severity: 'error',
        });
        return;
      }
      setModalOpen(false);
      setSnackbar({ open: true, message: 'Template saved.', severity: 'success' });
      await load();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setSaving(true);
    try {
      const res = await deleteWheelTemplate(pendingDelete.id);
      if (!isDailyPageApiSuccess(res)) {
        setSnackbar({
          open: true,
          message: getDailyPageErrorMessage(res, 'Failed to delete template.'),
          severity: 'error',
        });
        return;
      }
      setPendingDelete(null);
      setSnackbar({ open: true, message: 'Template deleted.', severity: 'success' });
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Reusable FE wheel sector libraries. Assign a template to an IST date from the day editor —
        that freezes a snapshot. Independent from the global default wheel.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New template
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell>Spins / day</TableCell>
            <TableCell>Sectors</TableCell>
            <TableCell>Enabled</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {templates.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography color="text.secondary">No templates yet.</Typography>
              </TableCell>
            </TableRow>
          ) : (
            templates.map((t) => (
              <TableRow key={t.id}>
                <TableCell>{t.title || `Template ${t.id}`}</TableCell>
                <TableCell>{t.spin_count_per_day ?? '—'}</TableCell>
                <TableCell>{t.sector_count ?? t.sectors?.length ?? 0}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={t.is_enabled === false ? 'Off' : 'On'}
                    color={t.is_enabled === false ? 'default' : 'success'}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(t)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setPendingDelete(t)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Dialog open={modalOpen} onClose={() => !saving && setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit template' : 'New wheel template'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Title"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                type="number"
                label="Spins per day"
                value={form.spin_count_per_day}
                onChange={(e) =>
                  setForm((p) => ({ ...p, spin_count_per_day: Number(e.target.value) }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.is_enabled}
                    onChange={(e) => setForm((p) => ({ ...p, is_enabled: e.target.checked }))}
                  />
                }
                label="Enabled"
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={1}>
            <Typography variant="subtitle2">Sectors</Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                setForm((p) => ({
                  ...p,
                  sectors: [...p.sectors, emptySector(p.sectors.length + 1)],
                }))
              }
            >
              Add sector
            </Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>FE</TableCell>
                <TableCell>Weight</TableCell>
                <TableCell>%</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {form.sectors.map((s, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={s.sector_type}
                      onChange={(e) => updateSector(idx, { sector_type: e.target.value })}
                      sx={{ minWidth: 130 }}
                    >
                      {SECTOR_TYPES.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={s.label || ''}
                      onChange={(e) => updateSector(idx, { label: e.target.value })}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={s.fe_amount ?? ''}
                      disabled={s.sector_type !== 'fe_reward'}
                      onChange={(e) => updateSector(idx, { fe_amount: Number(e.target.value) })}
                      sx={{ width: 90 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={s.weight}
                      onChange={(e) => updateSector(idx, { weight: Number(e.target.value) })}
                      sx={{ width: 90 }}
                    />
                  </TableCell>
                  <TableCell>{percents[idx]}%</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      disabled={form.sectors.length <= 1}
                      onClick={() =>
                        setForm((p) => ({
                          ...p,
                          sectors: p.sectors.filter((_, i) => i !== idx),
                        }))
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!pendingDelete} onClose={() => !saving && setPendingDelete(null)}>
        <DialogTitle>Delete template?</DialogTitle>
        <DialogContent>
          Delete “{pendingDelete?.title}”? Dates that already snapped this template keep their
          snapshot.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={saving}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default WheelTemplatesPage;
